import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { X, Shield, User, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestOverlay({ onLogin, onClose }) {
  const [loading, setLoading] = useState(false);

  const loginAsAdmin = async () => {
    setLoading(true);
    try {
      // Use test-login endpoint that bypasses email verification
      const response = await axios.post(`${API}/auth/test-login`, {
        email: 'admin@uniside.at',
        password: 'admin123'
      });
      toast.success('Als Admin eingeloggt');
      onClose(); // Close overlay after login
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      toast.error('Fehler beim Admin-Login: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loginAsStudent = async () => {
    setLoading(true);
    try {
      // Use test-login endpoint - auto-creates and verifies test accounts
      const response = await axios.post(`${API}/auth/test-login`, {
        email: 'test@student.at',
        password: 'test123'
      });
      
      // If onboarding not complete, complete it
      if (!response.data.user.onboardingComplete) {
        await axios.post(
          `${API}/users/complete-onboarding`,
          {
            profile: {
              firstName: 'Max',
              lastName: 'Testmann',
              phone: '+43 660 1234567',
              age: 24,
              gender: 'männlich',
              study: 'Informatik',
              hasDriverLicense: true,
              hasOwnCar: false,
              languageSkills: [
                { language: 'Deutsch', level: 'C2' },
                { language: 'Englisch', level: 'B2' }
              ],
              availableHoursPerWeek: 20,
              timeConstraints: 'Keine Einschränkungen',
              residenceState: 'Wien',
              residenceCity: 'Innere Stadt',
              preferredStates: ['Wien'],
              preferredDistricts: ['Innere Stadt'],
              preferredJobAreas: ['IT & Technik', 'Büro & Verwaltung'],
              profileImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzYzNjZmMSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1zaXplPSI0MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0id2hpdGUiPk1UPC90ZXh0Pjwvc3ZnPg=='
            }
          },
          { headers: { Authorization: `Bearer ${response.data.access_token}` } }
        );
        
        // Re-fetch user to get updated data
        const updatedResponse = await axios.post(`${API}/auth/test-login`, {
          email: 'test@student.at',
          password: 'test123'
        });
        toast.success('Als Student eingeloggt');
        onClose(); // Close overlay after login
        onLogin(updatedResponse.data.user, updatedResponse.data.access_token);
      } else {
        toast.success('Als Student eingeloggt');
        onClose(); // Close overlay after login
        onLogin(response.data.user, response.data.access_token);
      }
    } catch (error) {
      toast.error('Fehler beim Student-Login: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const registerNewUser = async () => {
    setLoading(true);
    try {
      const randomId = Math.floor(Math.random() * 10000);
      const testEmail = `wizard-test-${randomId}@student.at`;
      
      // Use test-login which auto-creates verified accounts
      const response = await axios.post(`${API}/auth/test-login`, {
        email: testEmail,
        password: 'test123'
      });
      
      toast.success('Neuer Test-Account erstellt - Wizard startet!');
      onLogin(response.data.user, response.data.access_token);
      onClose();
    } catch (error) {
      toast.error('Fehler beim Erstellen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-100 shadow-2xl rounded-2xl">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="close-test-overlay"
          >
            <X className="w-5 h-5" />
          </button>
          <CardTitle className="text-2xl font-outfit">Test-Zugang</CardTitle>
          <p className="text-slate-600 text-sm">Schneller Login für Tests</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Nur für Tests!</strong> Diese Funktion wird im Live-Betrieb entfernt.
            </p>
          </div>

          <Button
            onClick={loginAsAdmin}
            disabled={loading}
            className="w-full h-14 rounded-full bg-slate-900 hover:bg-slate-800 shadow-lg"
            data-testid="test-login-admin"
          >
            <Shield className="w-5 h-5 mr-2" />
            Als Admin einloggen
          </Button>

          <Button
            onClick={loginAsStudent}
            disabled={loading}
            className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
            data-testid="test-login-student"
          >
            <User className="w-5 h-5 mr-2" />
            Als Student einloggen
          </Button>

          <Button
            onClick={registerNewUser}
            disabled={loading}
            variant="outline"
            className="w-full h-14 rounded-full border-2 hover:bg-slate-50"
            data-testid="test-login-new-user"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Als neuer User (Wizard testen)
          </Button>

          <div className="text-xs text-slate-500 text-center space-y-1 pt-2">
            <p>Admin: admin@uniside.at / admin123</p>
            <p>Student: test@student.at / test123</p>
            <p>Neuer User: Wizard mit Bild-Upload testen</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
